import { Spin } from "components/ui/spin/Spin"
import { useViewerNavigation } from "hooks/viewerNavigation.hook"
import { useBidRequestFormData, useChatStore, useSeriesStore } from "store/aiAgent.store"
import { FeedbackForResponse } from "../../../FeedbackForResponse/FeedbackForResponse"
import { FailedStreamingResponseMessage } from "../FailedStreamingResponseMessage/FailedStreamingResponseMessage"
import { SeriesSelector } from "../SeriesSelector/SeriesSelector"
import { TransformStreamedData } from "../TransformStreamedData/TransformStreamedData"
import { SuccessChatAnswer } from "../SuccessChatAnswer/SuccessChatAnswer"
import { useMessage } from "hooks/message.hook"
import { BidRequestForm } from "../BidRequestForm/BidRequestForm"
import type { IAnswerMessage } from "types/nats.type"
import type {
	TAnnotationData,
	TBackendChatHistory,
	TChatAnswer,
	TChatQuestion,
	TPredefinedQuestions,
} from "types/aiAgent.type"
import style from "./ChatHistory.module.scss"

const {
	bubble,
	questionBlock,
	answer: answerClass,
	answerText,
	chatBottomBar,
	seriesResponse,
	bidRequestResponse,
} = style

type Props = {
	chatHistory: {
		questions: TChatQuestion[]
		answers: TChatAnswer[]
	}
	streamingAnswer: IAnswerMessage | undefined
	drawingId: number
	questionId: number | undefined
	predefinedQuestions: TPredefinedQuestions | undefined
	subject: string
	backendChatHistory: TBackendChatHistory[] | undefined
}

export const ChatHistory = ({
	chatHistory,
	streamingAnswer,
	drawingId,
	questionId,
	predefinedQuestions,
	subject,
}: Props) => {
	const { navigateToPage, navigateToParagraph } = useViewerNavigation()
	const { toolCallInfo } = useChatStore()
	const { hasActiveSeries, getActiveSeries, selectedSeries } = useSeriesStore()
	const { hasActiveBidRequest, getActiveBidRequest } = useBidRequestFormData()
	const { error: errorMessage } = useMessage()

	const referenceClickHandle = (page: string, quotation: string | undefined) => {
		navigateToPage(page, quotation)
	}

	const paragraphReferenceClickHandle = (pid: string) => {
		// Get paragraph data from toolCallInfo
		const paragraphData = toolCallInfo[pid]
		if (paragraphData && paragraphData.highlight_box && paragraphData.page_n) {
			const [x, y, x1, y2] = paragraphData.highlight_box
			const annotationData: TAnnotationData = {
				id: pid,
				x,
				y,
				width: x1,
				height: y2,
				pageNumber: paragraphData.page_n,
				content: paragraphData.text || undefined,
				// Pass along quads if available for better text highlighting
				// quads: paragraphData.quads,
				text: paragraphData.text,
			}
			navigateToParagraph(annotationData)
		} else {
			errorMessage("Paragraph reference didn't found coodinates")
		}
	}

	const renderAnswer = (answer: TChatAnswer, answerIndex: number, isCurrentlyStreaming: boolean) => {
		// Show failed message
		if (answer.content === "FAILED") {
			return <FailedStreamingResponseMessage />
		}

		// Show success message for bid request confirmation
		if (answer.content === "Bid Request confirmed and submitted!") {
			return <SuccessChatAnswer text={answer.content} />
		}

		// Check if this answer should show the series selector based on direct property
		const shouldShowSeries = answer.showSeriesSelector && hasActiveSeries()
		const seriesData = shouldShowSeries ? getActiveSeries() : null

		// Check if this answer should show the bid request form based on direct property
		const shouldShowBidRequestFields = answer.showBidRequestForm && hasActiveBidRequest()
		const activeBidRequestData = shouldShowBidRequestFields ? getActiveBidRequest() : null

		return (
			<>
				<div className={`${bubble} ${answerClass} ${answerText}`}>
					<TransformStreamedData
						content={answer.content}
						onReferenceClick={referenceClickHandle}
						onParagraphReferenceClick={paragraphReferenceClickHandle}
						streamingInProgress={isCurrentlyStreaming}
					/>
					<div className={chatBottomBar}>
						<FeedbackForResponse
							langsmithTraceId={chatHistory.questions[answerIndex]?.langsmithTraceId}
							drawingId={drawingId}
							questionId={questionId}
						/>
					</div>
				</div>
				{shouldShowSeries && seriesData ? (
					<div className={`${bubble} ${answerClass} ${answerText} ${seriesResponse}`}>
						<SeriesSelector
							langsmithTraceId={chatHistory.questions[answerIndex]?.langsmithTraceId}
							series={seriesData}
							predefinedQuestions={predefinedQuestions}
							subject={subject}
							answerIndex={answerIndex}
						/>
						<div className={chatBottomBar}>
							<FeedbackForResponse
								langsmithTraceId={chatHistory.questions[answerIndex]?.langsmithTraceId}
								drawingId={drawingId}
								questionId={questionId}
							/>
						</div>
					</div>
				) : null}
				{shouldShowBidRequestFields && activeBidRequestData ? (
					<div className={`${bubble} ${answerClass} ${answerText} ${bidRequestResponse}`}>
						<BidRequestForm
							bidRequestData={activeBidRequestData}
							langsmithTraceId={chatHistory.questions[answerIndex]?.langsmithTraceId}
							selectedSeries={selectedSeries}
							answerIndex={answerIndex}
						/>
						<div className={chatBottomBar}>
							<FeedbackForResponse
								langsmithTraceId={chatHistory.questions[answerIndex]?.langsmithTraceId}
								drawingId={drawingId}
								questionId={questionId}
							/>
						</div>
					</div>
				) : null}
			</>
		)
	}

	return (
		<>
			{chatHistory.questions.map((question, index) => {
				const isLastQuestion = index === chatHistory.questions.length - 1
				const answer = chatHistory.answers[index]
				const status = streamingAnswer?.status

				// Show spinner for the last question when:
				// 1. Status is STARTED or TOOL_CALL_INFO
				// 2. Streaming message is empty
				// 3. Status is not RUNING
				const shouldShowSpinner =
					isLastQuestion &&
					status !== "RUNING" &&
					(status === "STARTED" || status === "TOOL_CALL_INFO" || streamingAnswer?.message === "")

				// Check if this specific answer is currently being streamed
				const isCurrentlyStreaming =
					isLastQuestion &&
					streamingAnswer &&
					(status === "STARTED" ||
						status === "RUNNING" ||
						status === "PREDEFINED_QUESTION_SUMMARY" ||
						status === "TOOL_CALL_INFO")

				return (
					<div key={`qa-${index}`}>
						<div className={`${bubble} ${questionBlock}`}>
							<span>{question.text}</span>
						</div>
						{shouldShowSpinner ? (
							<Spin size="large" />
						) : answer ? (
							renderAnswer(answer, index, !!isCurrentlyStreaming)
						) : null}
					</div>
				)
			})}
		</>
	)
}
